import { AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface DeleteTemplateDialogProps {
  templateName: string;
  sharedAssets: Array<{
    assetId: number;
    originalName: string;
    filename: string;
    templatesUsingThis: Array<{
      id: number;
      name: string;
      description?: string;
    }>;
  }>;
  exclusiveAssets: Array<{
    assetId: number;
    originalName: string;
    filename: string;
  }>;
  onConfirm: (deleteAssets: boolean) => void;
  onCancel: () => void;
  isDeleting: boolean;
}

export default function DeleteTemplateDialog({
  templateName,
  sharedAssets,
  exclusiveAssets,
  onConfirm,
  onCancel,
  isDeleting,
}: DeleteTemplateDialogProps) {
  const hasExclusiveAssets = exclusiveAssets.length > 0;
  const hasSharedAssets = sharedAssets.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-red-900 dark:text-red-100">
                Delete Template: {templateName}
              </h2>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                This action cannot be undone
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Shared Assets Section */}
          {hasSharedAssets && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {sharedAssets.length} Shared Asset{sharedAssets.length !== 1 ? 's' : ''}
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                These assets are used in other templates and will remain in the library:
              </p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {sharedAssets.map((asset) => (
                  <div
                    key={asset.assetId}
                    className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-md"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {asset.originalName}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          Also used in:{' '}
                          {asset.templatesUsingThis.map((t) => t.name).join(', ')}
                        </p>
                      </div>
                      <Badge variant="outline" className="ml-2 text-green-700 dark:text-green-300">
                        Protected
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Exclusive Assets Section */}
          {hasExclusiveAssets && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {exclusiveAssets.length} Exclusive Asset{exclusiveAssets.length !== 1 ? 's' : ''}
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                These assets are only used in this template:
              </p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {exclusiveAssets.map((asset) => (
                  <div
                    key={asset.assetId}
                    className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-md"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {asset.originalName}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          File: {asset.filename}
                        </p>
                      </div>
                      <Badge variant="secondary" className="ml-2">
                        Unused elsewhere
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>

              {/* Warning about AWS deletion */}
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-md">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800 dark:text-yellow-200">
                    <p className="font-medium mb-1">AWS Storage Decision</p>
                    <p>
                      You can choose to keep these assets in AWS S3 (they'll remain in the asset library)
                      or delete them completely to save storage costs.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info box when no assets */}
          {!hasExclusiveAssets && !hasSharedAssets && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                This template has no assets and can be safely deleted.
              </p>
            </div>
          )}
        </div>

        {/* Footer / Actions */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isDeleting}
              className="w-full sm:w-auto bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </Button>

            {hasExclusiveAssets ? (
              <>
                <Button
                  onClick={() => onConfirm(false)}
                  disabled={isDeleting}
                  className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    <>Delete Template Only</>
                  )}
                </Button>
                <Button
                  onClick={() => onConfirm(true)}
                  disabled={isDeleting}
                  className="w-full sm:w-auto bg-red-700 hover:bg-red-800 text-white disabled:opacity-50"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    <>
                      Delete Template & AWS Assets ({exclusiveAssets.length})
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => onConfirm(false)}
                disabled={isDeleting}
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>Delete Template</>
                )}
              </Button>
            )}
          </div>

          {hasExclusiveAssets && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
              💡 Tip: Keep assets in library if you might reuse them later
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
